import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import * as React from "react";
import Chart from "react-apexcharts";
import moment from "moment";
import { useFormatAmount } from "../../../shared";
import { DashboardChartCard } from "./DashboardChartCard";
import {
  getXAxisTickAmount,
  useBaseChartOptions,
  useChartColors,
  useSalesTooltipRenderer,
} from "./chartHelpers";

export const DashboardGrossSalesChart = ({ title, data = [], index }) => {
  const formatAmount = useFormatAmount();
  const chartColors = useChartColors();
  const baseOptions = useBaseChartOptions();
  const renderSalesTooltip = useSalesTooltipRenderer();
  const [salesMode, setSalesMode] = React.useState("gross");
  const isGrossMode = salesMode === "gross";
  const activeColor = isGrossMode ? chartColors.gross : chartColors.net;
  const seriesKey = isGrossMode ? "net_grand_total_inc" : "net_grand_total_exc";
  const categories = data.map((item) => moment(item.sales_date).format("DD MMM"));
  const pointCount = categories.length;
  // value labels only while they have room; the axis and tooltip carry longer ranges
  const showValueLabels = pointCount <= 7;
  // thinner columns as the range grows so the gaps between them stay visible
  const columnWidth = pointCount <= 7 ? "28%" : pointCount <= 14 ? "40%" : "60%";

  const handleSalesModeChange = (_, newValue) => {
    if (newValue) {
      setSalesMode(newValue);
    }
  };

  const options = {
    ...baseOptions,
    chart: { ...baseOptions.chart, id: "dashboard-gross-sales-bar" },
    colors: [activeColor],
    xaxis: {
      ...baseOptions.xaxis,
      categories,
      tickAmount: getXAxisTickAmount(pointCount),
    },
    yaxis: {
      ...baseOptions.yaxis,
      min: 0,
      forceNiceScale: true,
      labels: {
        ...baseOptions.yaxis.labels,
        formatter: (value) => formatAmount(value),
      },
    },
    plotOptions: {
      bar: {
        columnWidth,
        borderRadius: 4,
        borderRadiusApplication: "end",
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: showValueLabels,
      offsetY: -18,
      style: {
        fontSize: "12px",
        fontWeight: 600,
        colors: [chartColors.label],
      },
      formatter: (value) => formatAmount(value),
    },
    tooltip: {
      ...baseOptions.tooltip,
      custom: ({ dataPointIndex }) => {
        const item = data[dataPointIndex] || {};
        return renderSalesTooltip(
          moment(item.sales_date).format("DD MMM YYYY"),
          item,
          "net_grand_total_inc",
          "net_grand_total_exc"
        );
      },
    },
  };

  const series = [
    {
      name: isGrossMode ? "Gross Sales" : "Net Sales",
      data: data.map((item) => Number(item[seriesKey] || 0)),
    },
  ];

  return (
    <DashboardChartCard
      title={title}
      isEmpty={!data.length}
      action={
        <ToggleButtonGroup
          value={salesMode}
          exclusive
          onChange={handleSalesModeChange}
          size="small"
        >
          <ToggleButton value="gross">Gross Sales</ToggleButton>
          <ToggleButton value="net">Net Sales</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      <Box sx={{ px: "8px", pb: "8px" }}>
        <Chart
          key={index}
          options={options}
          series={series}
          type="bar"
          width="100%"
          height="361px"
        />
      </Box>
    </DashboardChartCard>
  );
};
